import React, { useCallback, useMemo, useState, useRef } from 'react';
import { BusinessSMSEditForm } from './BusinessSMSEditForm';
import { useRecoilStateLoadable, useSetRecoilState } from 'recoil';
import { IUserAtom, user } from '../../../recoil/atoms/user';
import {
  loadableEnum,
  useMakePHPRequest,
} from '../../../common/useMakePHPRequest';
import { useSnackbarWithStyles } from '../../components/notifications/SnackbarProviderWithStyles';
import {
  endpointEnum,
  makeApiRequestCached,
} from '../../../services/rest/makeAPIRequest';
import { Duration } from 'luxon';
import { Business } from '../../../lib/kixie-entities/tables/Business';
import { BusinessHarassmentPolicy } from '../../../lib/kixie-entities/tables/BusinessHarassmentPolicy';
import { useMutation } from 'react-query';
import { getApiMutation } from '../../../services/rest/getApiMutation';
import { CancelTokenSource } from 'axios';
import { refetchToggle } from '../../../recoil/selectors/makePHPRequest';
export interface IBusinessSMSNumbers {
  businessSMSId?: number;
  numberId: number;
  action: 'add' | 'delete' | 'update';
}

export interface IBusinessSMSTeam {
  businessSMSId?: number;
  teamId: number;
  businessId?: number;
  action?: 'add' | 'delete' | 'update';
  memberCount?: number;
  teamName?: string;
  teamname?: string;
}

export interface IBusinessSMS {
  businessSMSId?: number;
  businessId: number;
  active: number;
  replyDurationMin: number;
  smsHours?: unknown;
  useBusinessHoursForSMS: number;
  afterHoursActionType: 'none' | 'unknown' | 'sendSMS';
  smsTemplateId?: number | null;
  smsMessage: string;
  createdByUserId?: number;
  createdDate?: Date;
  updatedDate?: Date;
  teams?: IBusinessSMSTeam[];
  numbers?: IBusinessSMSNumbers[];
  memberCount?: number;
  useCustomSMSMessage?: 0 | 1;
  days?: number;
  hours?: number;
  minutes?: number;
  name?: string;
}

export interface IBusinessSMSFormSubmit extends IBusinessSMS {
  initialValues: IBusinessSMS;
}

interface IQueueEditDialog {
  id?: string;
  show: boolean;
  onHide: () => void;
  setUpdatedRowId?: (rowId: string | undefined) => void;
}

/**
 * Function to hide modal
 *
 * @callback onHideCallback
 * @param {Object} event Synthetic react event
 */

/**
 * Queue editing component
 *
 * @param {Object} props
 * @param {string} [props.id] Queue id
 * @param {boolean} props.show True if modal shown
 * @param {onHideCallback} props.onHide Function to hide modal
 *
 * @see https://github.com/microsoft/vscode/issues/41296
 */
export function BusinessSMSEditDialog({
  id,
  show,
  onHide,
  setUpdatedRowId,
}: IQueueEditDialog) {
  const { enqueueSnackbarWithStyles } = useSnackbarWithStyles();
  const [submitState, setSubmitState] = useState(loadableEnum.EMPTY);
  const [, setDeleteCancelTokenSource] = useState<
    CancelTokenSource | undefined
  >();
  const [userAtomLoadable, setUserAtom] = useRecoilStateLoadable<
    IUserAtom | Record<string, unknown>
  >(user);

  const userAtomContents = userAtomLoadable?.contents as IUserAtom;
  const phpRequest = useMakePHPRequest({
    userAtom: userAtomContents,
  });
  const mutation = useMutation(
    getApiMutation(userAtomContents, setUserAtom, setDeleteCancelTokenSource)
  );

  const [_, businessSMSLoadable, setRefetchForBusinessSMS] = id
    ? makeApiRequestCached(
        {
          method: 'get',
          // stage: 'itn',
          domain: process.env.VITE_APP_CDK_DOMAIN,
          endpoint:
            endpointEnum.BUSINESS_SMS_BY_BUSINESS +
            `/${userAtomContents?.businessid}` +
            `/${id}`,
          payloadJsonString: JSON.stringify({
            apikey: userAtomContents?.apikey,
            businessid: userAtomContents?.businessid,
            businessId: userAtomContents?.businessid,
          }),
        },
        (loadable, cached) =>
          loadable.contents?.response?.data?.result ?? cached.current
      )
    : [null, null, null];
  const businessSMSInboxCached = useRef({} as any);
  businessSMSInboxCached.current = useMemo(
    () =>
      id && businessSMSLoadable?.state === 'hasValue'
        ? {
            id:
              businessSMSLoadable?.contents?.response?.data?.data
                ?.businessSMSId,
            ...businessSMSLoadable?.contents?.response?.data?.data,
          }
        : businessSMSInboxCached?.current,
    [businessSMSLoadable, userAtomContents]
  );

  const setRefetchToggleForBusinessSMSNumbers = useSetRecoilState(
    refetchToggle(
      endpointEnum.BUSINESS_SMS_NUMBERS +
        `/${userAtomContents?.businessid ?? ''}`
    )
  );

  const [businessCached, businessLoadable] = makeApiRequestCached<
    Business & {
      // eslint-disable-next-line func-call-spacing
      businessHarassmentPolicies: (BusinessHarassmentPolicy & {
        days: number;
        hours: number;
        minutes: number;
      })[];
    }
  >(
    {
      endpoint: `${endpointEnum.GET_BUSINESSES}/${userAtomContents.businessid}`,
      method: 'get',
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.map((business: Business) => ({
        ...business,
        businessHarassmentPolicies: business.businessHarassmentPolicies?.map(
          (policy) => {
            const luxonDuration = Duration.fromMillis(
              policy.durationMin * 60 * 1000
            )
              .shiftTo('days', 'hours', 'minutes', 'seconds', 'milliseconds')
              .toObject();
            return {
              ...policy,
              days: luxonDuration.days,
              hours: luxonDuration.hours,
              minutes: luxonDuration.minutes,
            };
          }
        ),
      }))?.[0] ?? cached.current
  );

  const businessHoursMemo = useMemo(
    () =>
      businessCached.current?.businessDetails?.businessHours ?? {
        1: {
          day: 'Monday',
          hours: [],
        },
        2: {
          day: 'Tuesday',
          hours: [],
        },
        3: {
          day: 'Wednesday',
          hours: [],
        },
        4: {
          day: 'Thursday',
          hours: [],
        },
        5: {
          day: 'Friday',
          hours: [],
        },
        6: {
          day: 'Saturday',
          hours: [],
        },
        7: {
          day: 'Sunday',
          hours: [],
        },
      },
    [businessLoadable, businessCached, businessLoadable]
  );

  // for new business sms rule
  const businessSMSRuleForCreate = useMemo<IBusinessSMS>(
    () => ({
      name: '',
      businessId: userAtomContents?.businessid,
      active: 1,
      replyDurationMin: 30,
      days: 0,
      hours: 0,
      minutes: 30,
      smsHours: businessHoursMemo,
      useBusinessHoursForSMS: 1,
      createdByUserId: userAtomContents?.userid,
      smsTemplateId: null,
      smsMessage: '',
      afterHoursActionType: 'sendSMS',
      useCustomSMSMessage: 1,
      teams: [],
      numbers: [],
    }),
    [userAtomContents, businessCached, businessLoadable]
  );

  const JSONParser = (jsonString: string) => {
    if (typeof jsonString === 'object') {
      return jsonString;
    }
    try {
      return JSON.parse(jsonString);
    } catch (e) {
      return businessHoursMemo;
    }
  };

  const businessSMSRuleForEdit = useMemo<IBusinessSMS>(
    () => ({
      name: businessSMSInboxCached?.current?.name ?? '',
      businessId: businessSMSInboxCached?.current?.businessId ?? null,
      active: businessSMSInboxCached?.current?.active ?? 1,
      replyDurationMin: businessSMSInboxCached?.current?.replyDurationMin ?? 30,
      days:
        Math.floor(
          (businessSMSInboxCached?.current?.replyDurationMin ?? 30) / (24 * 60)
        ) ?? 0,
      hours:
        Math.floor(
          (businessSMSInboxCached?.current?.replyDurationMin ?? 30) / 60
        ) % 24 ?? 0,
      minutes:
        (businessSMSInboxCached?.current?.replyDurationMin ?? 30) % 60 ?? 30,
      smsHours: businessSMSInboxCached?.current?.smsHours
        ? JSONParser(businessSMSInboxCached?.current?.smsHours)
        : businessHoursMemo,
      useBusinessHoursForSMS:
        businessSMSInboxCached?.current?.useBusinessHoursForSMS ?? 1,
      createdByUserId: businessSMSInboxCached?.current?.createdByUserId ?? null,
      smsTemplateId: businessSMSInboxCached?.current?.smsTemplateId ?? null,
      smsMessage: businessSMSInboxCached?.current?.smsMessage ?? '',
      afterHoursActionType:
        businessSMSInboxCached?.current?.afterHoursActionType ?? 'sendSMS',
      useCustomSMSMessage:
        businessSMSInboxCached?.current?.useCustomSMSMessage ?? 1,
      teams: businessSMSInboxCached?.current?.teams ?? [],
      numbers: businessSMSInboxCached?.current?.numbers ?? [],
    }),
    [
      businessSMSInboxCached,
      businessSMSLoadable,
      userAtomContents,
      businessCached,
    ]
  );
  const handleSubmit = useCallback(
    async ({
      businessId,
      active,
      replyDurationMin,
      smsHours,
      useBusinessHoursForSMS,
      afterHoursActionType,
      smsTemplateId,
      smsMessage,
      createdByUserId,
      teams,
      numbers,
      useCustomSMSMessage,
      days,
      hours,
      minutes,
      name,
      initialValues,
    }: IBusinessSMSFormSubmit) => {
      setSubmitState(loadableEnum.LOADING);
      const replyDurationInMin =
        (days ?? 0) * (24 * 60) + (hours ?? 0) * 60 + (minutes ?? 0);
      try {
        const teamsToAdd =
          teams
            ?.filter(
              (updatedTeam) =>
                !initialValues?.teams?.find(
                  (initialTeam) => initialTeam?.teamId === updatedTeam?.teamId
                )
            )
            ?.map((elem) => ({
              ...elem,
              action: 'add',
            })) ?? [];
        const teamsToDelete =
          initialValues?.teams
            ?.filter(
              (initialTeam) =>
                !teams?.find(
                  (updatedTeam) => initialTeam?.teamId === updatedTeam?.teamId
                )
            )
            .map((elem) => ({
              ...elem,
              action: 'delete',
            })) ?? [];
        const teamsToEdit = teamsToAdd?.concat(teamsToDelete);
        const numbersToAdd =
          numbers
            ?.filter(
              (updatedNum) =>
                !initialValues?.numbers?.find(
                  (initialNum) => initialNum?.numberId === updatedNum?.numberId
                )
            )
            ?.map((elem) => ({
              ...elem,
              action: 'add',
            })) ?? [];
        const numbersToDelete =
          initialValues?.numbers
            ?.filter(
              (initialNum) =>
                !numbers?.find(
                  (updatedNum) => initialNum?.numberId === updatedNum?.numberId
                )
            )
            .map((elem) => ({
              ...elem,
              action: 'delete',
            })) ?? [];
        const numbersToEdit = numbersToAdd?.concat(numbersToDelete);
        const response = await mutation.mutateAsync({
          method: 'post',
          // stage: 'itn',
          domain: process.env.VITE_APP_CDK_DOMAIN,
          endpoint: endpointEnum.BUSINESS_SMS,
          payload: {
            businessSMSId: id ?? 0,
            apikey: userAtomContents?.apikey,
            businessid: businessId,
            businessId,
            active: active ? '1' : '0',
            replyDurationMin: replyDurationInMin,
            smsHours,
            useBusinessHoursForSMS: useBusinessHoursForSMS ? '1' : '0',
            afterHoursActionType,
            smsTemplateId: smsTemplateId ?? 0,
            smsMessage,
            createdByUserId,
            teams: teamsToEdit,
            numbers: numbersToEdit,
            useCustomSMSMessage: useCustomSMSMessage ? '1' : '0',
            days,
            hours,
            minutes,
            name,
          },
        });
        setRefetchToggleForBusinessSMSNumbers({});
        if ((response as any)?.data?.success) {
          setSubmitState(loadableEnum.HAS_VALUE);
          enqueueSnackbarWithStyles({
            message: id
              ? 'Successfully updated team SMS rule'
              : 'Successfully created team SMS rule',
            alertProps: {
              severity: 'success',
            },
          });
        } else {
          let errorMessage = id
            ? 'There was an error updating the team SMS rule'
            : 'There was an error creating the team SMS rule';
          let severity: any = 'error';
          if (
            (response as any)?.data?.data?.error?.slice?.(0, 12) ===
            'ER_DUP_ENTRY'
          ) {
            errorMessage =
              'SMS Inbox name already taken, please choose another name.';
            severity = 'warning';
          }

          setSubmitState(loadableEnum.HAS_ERROR);
          enqueueSnackbarWithStyles({
            message: errorMessage,
            alertProps: {
              severity,
            },
          });
        }
      } catch (e) {
        setSubmitState(loadableEnum.HAS_ERROR);
        let errorMessage = id
          ? 'There was an error updating the team SMS rule'
          : 'There was an error creating the team SMS rule';
        if (
          (e as any)?.response?.data?.data?.error?.slice?.(0, 12) ===
          'ER_DUP_ENTRY'
        ) {
          errorMessage =
            'SMS Inbox name already taken, please choose another name.';
        }
        enqueueSnackbarWithStyles({
          message: errorMessage,
          alertProps: {
            severity: 'error',
          },
        });
      }
      setRefetchForBusinessSMS?.({});
      onHide();
    },
    [
      id,
      userAtomLoadable,
      setSubmitState,
      setUpdatedRowId,
      onHide,
      phpRequest,
      enqueueSnackbarWithStyles,
    ]
  );

  return (
    <BusinessSMSEditForm
      id={id}
      businessid={userAtomContents?.businessid}
      show={show}
      businessSMSRule={id ? businessSMSRuleForEdit : businessSMSRuleForCreate}
      isLoading={
        businessLoadable?.state === 'loading' ||
        businessSMSLoadable?.state === 'loading' ||
        submitState === loadableEnum.LOADING
      }
      onSubmit={handleSubmit}
      onHide={onHide}
      businessLoadable={businessLoadable}
      businessCached={businessCached}
    />
  );
}
